import { useState } from "react";
import { toast } from "sonner";
import { CircleNotch, HandHeart } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const AVAIL = ["Weekday evenings", "Weekend evenings", "Both", "Occasional / flexible"];

export default function VolunteerForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    availability: "",
    skills: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.availability) {
      toast.error("Please fill in your name, email, and availability.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/volunteers", {
        ...form,
        phone: form.phone || null,
        skills: form.skills || null,
        message: form.message || null,
      });
      toast.success("Thank you — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", availability: "", skills: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="volunteer" data-testid="volunteer-section" className="py-24 md:py-32 bg-[#FDFBF7]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <p className="label-eyebrow mb-5">Lend a hand</p>
          <h2 className="font-headline font-light text-4xl md:text-5xl text-[#1F1A17] leading-tight">
            Cook, serve, listen, <span className="italic text-[#C25934]">be here</span>.
          </h2>
          <p className="mt-6 text-lg text-[#5A524A] leading-relaxed">
            Volunteers are the quiet engine of every evening. Some help us cook. Some help us
            clean. Some just bring themselves and a willingness to sit with a stranger.
          </p>
          <p className="mt-4 text-[#5A524A] leading-relaxed">
            Whatever you can offer — an evening a week, an hour a month — we'll find a place
            for you at the table.
          </p>
          <p className="mt-8 font-accent text-3xl text-[#5C6B51]">
            come as you are.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="md:col-span-7 bg-[#F4EFE6] border border-[#EADAB9] rounded-3xl p-8 md:p-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow block mb-2">Name</label>
              <input
                data-testid="volunteer-name"
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder="Your full name"
                className="hearth-input"
                required
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                data-testid="volunteer-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@example.com"
                className="hearth-input"
                required
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Phone (optional)</label>
              <input
                data-testid="volunteer-phone"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="(555) 123-4567"
                className="hearth-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Availability</label>
              <select
                data-testid="volunteer-availability"
                value={form.availability}
                onChange={update("availability")}
                className="hearth-input appearance-none cursor-pointer"
                required
              >
                <option value="" disabled>Pick what fits</option>
                {AVAIL.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="label-eyebrow block mb-2">Skills you'd like to share</label>
            <input
              data-testid="volunteer-skills"
              type="text"
              value={form.skills}
              onChange={update("skills")}
              placeholder="e.g. home cooking, conversation, music, first aid"
              className="hearth-input"
            />
          </div>

          <div className="mt-4">
            <label className="label-eyebrow block mb-2">Anything else?</label>
            <textarea
              data-testid="volunteer-message"
              value={form.message}
              onChange={update("message")}
              rows={4}
              placeholder="Share whatever feels useful — we read every word."
              className="hearth-input resize-none"
            />
          </div>

          <button
            data-testid="volunteer-submit"
            type="submit"
            disabled={loading}
            className="btn-hearth mt-7 inline-flex items-center justify-center gap-3 rounded-full bg-[#5C6B51] hover:bg-[#434E3B] disabled:opacity-60 text-white px-8 py-4 text-base font-semibold w-full md:w-auto"
          >
            {loading ? (
              <>
                <CircleNotch size={20} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                <HandHeart size={20} weight="duotone" /> Sign me up
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
